import React, { useCallback } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { Autocomplete, Box, Grid, Paper, TextField, Typography } from '@mui/material';
import { getTranslatedText } from '../../../../utils/getTranslatedText';
import { useTranslation } from '../../../../utils/getLocalString';
import { styled } from '@mui/material/styles';

// Original class: customPopper
const StyledCustomPopper = styled(Autocomplete)(theme => ({
  '& + .MuiAutocomplete-popper': {
    zIndex: 1,
    position: 'static!important',
    transform: 'none!important',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
}));
// Original class: fieldsListItem
const StyledFieldsListItem = styled(Box)(({ theme }) => ({
  height: 36,
  padding: theme.spacing(2, 2),
  cursor: 'pointer',
  userSelect: 'none',

  '&:hover': {
    backgroundColor: '#F1F2F8',
  },

  '& .MuiAutocomplete-option': {
    '&.Mui-focused': {
      backgroundColor: 'red',
    },
  },
}));
// Original class: optionsFilterInput
const StyledOptionsFilterInput = styled(TextField)(({ theme }) => ({
  '&.MuiTextField-root': {
    '& .MuiInputBase-root': {
      padding: 0,
      borderRadius: theme.spacing(4, 4, 0, 0),
      backgroundColor: '#ffffff !important',
      borderBottom: '1px solid #E4E4EF',
      '& input': {
        padding: theme.spacing(2, 2),
      },
      '& .MuiOutlinedInput-notchedOutline': {
        border: 0,
      },
      '&:not(.Mui-error):hover': {
        borderBottom: '1px solid #E4E4EF',
      },
    },
  },
}));
// Original class: fieldsList
const StyledFieldsList = styled(Paper)(({ theme }) => ({
  '&.MuiPaper-root': {
    boxShadow: 'none',
    overflow: 'auto',
    borderRadius: theme.spacing(0, 0, 4, 4),
    '&::-webkit-scrollbar': {
      width: '2px',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#7174AC',
      borderRadius: '5px',
    },
    '& .MuiAutocomplete-option': {
      padding: 0,
    },
    '& .MuiAutocomplete-listbox': {
      padding: 0,
      //height: 324,
      maxHeight: 'none',
    },
  },
}));
const DragDropLists = ({
  firstListOptions,
  secondListOptions,
  getOptionLabel,
  setParentState,
  nonDraggableIds = [],
  componentParent = 'default',
}) => {
  const getLocalString = useTranslation();

  const handleMoveField = useCallback(
    ({ currentPosition, newPosition, direction }) => {
      // LEFT TO LEFT
      if (direction === 'ltl') {
        setParentState(prevState => {
          const [currentElement] = prevState.availableFields.splice(currentPosition, 1);
          if (newPosition < 0) {
            prevState.availableFields.push(currentElement);
          } else {
            prevState.availableFields.splice(newPosition, 0, currentElement);
          }
          return {
            ...prevState,
            availableFields: prevState.availableFields,
            selectedFields: prevState.selectedFields,
          };
        });
      }
      // RIGHT TO RIGHT
      if (direction === 'rtr') {
        setParentState(prevState => {
          const [currentElement] = prevState.selectedFields.splice(currentPosition, 1);
          if (newPosition < 0) {
            prevState.selectedFields.push(currentElement);
          } else {
            prevState.selectedFields.splice(newPosition, 0, currentElement);
          }
          return {
            ...prevState,
            availableFields: prevState.availableFields,
            selectedFields: prevState.selectedFields,
          };
        });
      }
      if (direction === 'left') {
        setParentState(prevState => {
          const [currentElement] = prevState.selectedFields.splice(currentPosition, 1);
          if (newPosition < 0) {
            prevState.availableFields.push(currentElement);
          } else {
            prevState.availableFields.splice(newPosition, 0, currentElement);
          }
          return {
            ...prevState,
            availableFields: prevState.availableFields,
            selectedFields: prevState.selectedFields,
          };
        });
      }

      if (direction === 'right') {
        setParentState(prevState => {
          const [currentElement] = prevState.availableFields.splice(currentPosition, 1);
          if (newPosition < 0) {
            prevState.selectedFields.push(currentElement);
          } else {
            prevState.selectedFields.splice(newPosition, 0, currentElement);
          }
          return {
            ...prevState,
            availableFields: prevState.availableFields,
            selectedFields: prevState.selectedFields,
          };
        });
      }
    },
    [setParentState],
  );
  const onDragEnd = useCallback(
    result => {
      if (!result.destination) {
        return;
      }
      const { source, destination } = result;
      if (nonDraggableIds.length > 0) {
        if (destination.droppableId === 'selectedFields' && (destination.index === 0 || destination.index === 1)) {
          return;
        }
      }
      if (source.droppableId !== destination.droppableId) {
        if (destination.droppableId === 'selectedFields') {
          handleMoveField({
            currentPosition: source.index,
            newPosition: destination.index,
            direction: 'right',
          });
        } else if (
          destination.droppableId === 'availableFields' &&
          secondListOptions[source.index]?.fieldId !== 'resourcePlan.monthly_distribution'
        ) {
          handleMoveField({
            currentPosition: source.index,
            newPosition: destination.index,
            direction: 'left',
          });
        }
      } else {
        if (destination.droppableId === 'selectedFields') {
          handleMoveField({
            currentPosition: source.index,
            newPosition: destination.index,
            direction: 'rtr',
          });
        } else if (destination.droppableId === 'availableFields') {
          handleMoveField({
            currentPosition: source.index,
            newPosition: destination.index,
            direction: 'ltl',
          });
        }
      }
    },
    [handleMoveField, nonDraggableIds.length, secondListOptions],
  );

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Grid
        size={'grow'}
        container
        sx={{
          marginTop: '20px',
          overflow: 'hidden',
          alignItems: 'stretch',
          flexWrap: 'nowrap',
          minHeight: '300px',
        }}
      >
        <Box
          sx={{
            overflow: 'hidden',
            marginRight: '10px',
            width: '50%',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              maxHeight: '100%',
              overflow: 'hidden',
            }}
          >
            <Typography
              variant="h5"
              sx={{
                marginLeft: 3,
              }}
            >
              {componentParent === 'agile'
                ? getLocalString('labels', 'avalibleColumns')
                : getLocalString('labels', 'avalibleColumns')}
            </Typography>
            <StyledCustomPopper
              open
              multiple
              disablePortal
              disableClearable
              options={firstListOptions.sort((a, b) => {
                const ruCollator = new Intl.Collator('ru-RU');
                const firstTranslatedValue = a.fieldName.toLowerCase();
                const secondTranslatedValue = b.fieldName.toLowerCase();
                return ruCollator.compare(firstTranslatedValue, secondTranslatedValue);
              })}
              getOptionLabel={getOptionLabel}
              filterOptions={(options, state) => {
                let newOptions = [];
                options.forEach(element => {
                  const firstTranslatedValue = element.fieldName.toLowerCase();
                  if (firstTranslatedValue.replace(',', '').toLowerCase().includes(state.inputValue.toLowerCase())) {
                    newOptions.push(element);
                  }
                });
                return newOptions;
              }}
              renderOption={params => {
                const paramName = params.key.split(';').reverse()[0];
                const paramId = params.key.split(';').reverse()[1];
                let index = firstListOptions.indexOf(firstListOptions.find(item => item.fieldId === paramId));
                const isBlocked = nonDraggableIds.includes(paramId);
                return (
                  <Draggable key={paramId} draggableId={paramId} index={index} isDragDisabled={isBlocked}>
                    {provided => {
                      return (
                        <StyledFieldsListItem
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          onClick={() => {
                            if (!isBlocked) {
                              handleMoveField({
                                currentPosition: index,
                                newPosition: -1,
                                direction: 'right',
                              });
                            }
                          }}
                        >
                          <Grid key={params['data-option-index']}>
                            <Typography
                              variant="h5"
                              sx={{
                                whiteSpace: 'nowrap',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {paramName}
                            </Typography>
                          </Grid>
                        </StyledFieldsListItem>
                      );
                    }}
                  </Draggable>
                );
              }}
              renderInput={params => {
                return (
                  <Box key={params.value} onMouseDown={params.inputProps.onMouseDown}>
                    <StyledOptionsFilterInput
                      variant="outlined"
                      {...params}
                      placeholder={getLocalString('labels', 'input')}
                    />
                  </Box>
                );
              }}
              // renderTags={props.renderFirstStepTags}
              noOptionsText={<Typography variant="h5">{getLocalString('errors', 'noResult')}</Typography>}
              slots={{
                paper: params => {
                  return (
                    <Droppable droppableId="availableFields">
                      {provided => {
                        return (
                          <>
                            {provided.placeholder}
                            <StyledFieldsList>
                              <Grid container direction="column" ref={provided.innerRef} {...provided.droppableProps}>
                                {params.children}
                              </Grid>
                            </StyledFieldsList>
                          </>
                        );
                      }}
                    </Droppable>
                  );
                },
              }}
            />
          </Box>
        </Box>
        <Box
          sx={{
            overflow: 'hidden',
            marginLeft: '10px',
            width: '50%',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              maxHeight: '100%',
              overflow: 'hidden',
            }}
          >
            <Typography
              variant="h5"
              sx={{
                marginLeft: 3,
              }}
            >
              {componentParent === 'agile'
                ? getLocalString('labels', 'avalibleColumns')
                : getLocalString('labels', 'selectedColumns')}
            </Typography>
            <StyledCustomPopper
              multiple
              disableClearable
              disablePortal
              open
              options={secondListOptions}
              getOptionLabel={getOptionLabel}
              filterOptions={(options, state) => {
                let newOptions = [];
                options.forEach(element => {
                  const firstTranslatedValue = element.fieldName.toLowerCase();
                  if (firstTranslatedValue.replace(',', '').toLowerCase().includes(state.inputValue.toLowerCase())) {
                    newOptions.push(element);
                  }
                });
                return newOptions;
              }}
              renderOption={params => {
                const paramName = params.key.split(';').reverse()[0];
                const paramId = params.key.split(';').reverse()[1];
                const isBlocked = nonDraggableIds.includes(paramId);
                let index = secondListOptions.indexOf(secondListOptions.find(item => item.fieldId === paramId));
                return (
                  <Draggable key={params.id} draggableId={params.id} index={index} isDragDisabled={isBlocked}>
                    {provided => {
                      return (
                        <Box
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          onClick={() => {
                            if (!isBlocked && !(paramId === 'resourcePlan.monthly_distribution')) {
                              handleMoveField({
                                currentPosition: index,
                                newPosition: 0,
                                direction: 'left',
                              });
                            }
                          }}
                        >
                          <StyledFieldsListItem>
                            <Typography
                              variant="h5"
                              sx={{
                                whiteSpace: 'nowrap',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {paramName}
                            </Typography>
                          </StyledFieldsListItem>
                        </Box>
                      );
                    }}
                  </Draggable>
                );
              }}
              renderInput={params => {
                return (
                  <Box key={params.value} onMouseDown={params.inputProps.onMouseDown}>
                    <StyledOptionsFilterInput
                      variant="outlined"
                      {...params}
                      placeholder={getLocalString('labels', 'input')}
                    />
                  </Box>
                );
              }}
              noOptionsText={<Typography variant="h5">{getLocalString('errors', 'noResult')}</Typography>}
              slots={{
                paper: params => {
                  return (
                    <Droppable droppableId="selectedFields">
                      {provided => (
                        <>
                          {provided.placeholder}
                          <StyledFieldsList ref={provided.innerRef} {...provided.droppableProps}>
                            {params.children}
                          </StyledFieldsList>
                        </>
                      )}
                    </Droppable>
                  );
                },
              }}
            />
          </Box>
        </Box>
      </Grid>
    </DragDropContext>
  );
};

export default DragDropLists;
